/**
 * SplitPane Component
 *
 * An orientation-agnostic two-pane layout with drag-to-resize functionality.
 * Supports both horizontal (left/right) and vertical (top/bottom) splits.
 *
 * Features (phased implementation):
 * - Phase 1: Static layout with orientation support and placeholder divider
 * - Phase 2: Pointer-event drag-to-resize, min/max constraints, mobile awareness
 * - Phase 3: localStorage persistence, double-click reset
 * - Phase 4: Controlled size prop and onResize callback for parent observation
 *
 * Designed for composition — nest SplitPane instances to create multi-pane layouts.
 *
 * Supports two modes:
 * - Uncontrolled (default): internal state manages size, initialized from
 *   localStorage (if storageKey) or defaultSize.
 * - Controlled: parent provides `size` prop and responds to `onResize` callback.
 *   During active drag, external `size` changes are deferred until drag ends.
 *
 * Mode-switch contract: toggling between controlled and uncontrolled at runtime
 * is not supported. If it happens, the last internal size is retained.
 */

import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Brand } from '@enfomentis/shared/config';

import { useIsDesktop } from '@/hooks';

// =============================================================================
// Types
// =============================================================================

export interface SplitPaneProps {
  /** Split direction: 'horizontal' (left/right) or 'vertical' (top/bottom) */
  orientation: 'horizontal' | 'vertical';
  /** Content for the first pane (left in horizontal, top in vertical) */
  firstContent: ReactNode;
  /** Content for the second pane (right in horizontal, bottom in vertical) */
  secondContent: ReactNode;
  /** Default size of the first pane in pixels (width for horizontal, height for vertical) */
  defaultSize: number;
  /**
   * Controlled size of the first pane in pixels. When provided, overrides
   * internal state (except during active drag). Omit for uncontrolled mode.
   *
   * Contract: in controlled mode, update `size` synchronously in the
   * `onResize` handler (call setState immediately). Debounce persistence
   * side effects, not the UI state — a stale `size` after drag end causes
   * visual snapback.
   */
  size?: number;
  /**
   * Callback fired when pane size changes (drag end, double-click reset,
   * container resize normalization). Reports pixel size and container
   * dimension for proportional weight calculation.
   *
   * Does NOT fire during drag move — only on drag completion.
   */
  onResize?: (newSize: number, containerDimension: number) => void;
  /** Minimum size of the first pane in pixels (default: 100) */
  minSize?: number;
  /** Maximum size — number for pixels, string for percentage e.g. "50%" (default: "50%") */
  maxSize?: number | string;
  /** localStorage key suffix for persisting size (prefixed with Brand.cachePrefix). When omitted, no persistence. */
  storageKey?: string;
  /** Size to reset to on double-click. When omitted, resets to `defaultSize`. */
  resetSize?: number;
  /**
   * Temporary size override — forces this exact pixel size for the first pane,
   * hides the divider, and disables drag/resize interaction. Internal pane size
   * state is NOT modified; when `forcedSize` is removed, the pane reverts to
   * its prior internal size automatically.
   */
  forcedSize?: number;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Total space occupied by the divider hit area.
 * Horizontal: px-1 padding (4px × 2) + w-1 inner (4px) = 12px
 * Vertical: py-1 padding (4px × 2) + h-1 inner (4px) = 12px
 */
const DIVIDER_FOOTPRINT = 12;

/**
 * Parse maxSize prop to pixel value.
 * Supports absolute pixels (number) and percentage strings (e.g., "50%").
 */
function parseMaxSize(
  maxSize: number | string,
  containerDimension: number
): number {
  if (typeof maxSize === 'number') {
    return maxSize;
  }
  // Parse percentage string (e.g., "50%")
  const match = maxSize.match(/^(\d+(?:\.\d+)?)%$/);
  if (match && match[1]) {
    const percent = parseFloat(match[1]);
    return Math.floor((containerDimension * percent) / 100);
  }
  // Fallback if invalid format — allow full container
  return containerDimension;
}

/**
 * Build full localStorage key with brand prefix.
 */
function buildStorageKey(suffix: string): string {
  return `${Brand.cachePrefix}-${suffix}`;
}

/**
 * Read number from localStorage with fallback.
 */
function readStoredSize(key: string, fallback: number): number {
  try {
    const stored = localStorage.getItem(key);
    if (stored !== null) {
      const parsed = JSON.parse(stored);
      if (typeof parsed === 'number' && !isNaN(parsed)) {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return fallback;
}

/**
 * Write number to localStorage.
 */
function writeStoredSize(key: string, value: number): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors (quota exceeded, etc.)
  }
}

// =============================================================================
// Component
// =============================================================================

export const SplitPane: React.FC<SplitPaneProps> = ({
  orientation,
  firstContent,
  secondContent,
  defaultSize,
  size,
  onResize,
  minSize = 100,
  maxSize = '50%',
  storageKey,
  resetSize,
  forcedSize,
}) => {
  // ---------------------------------------------------------------------------
  // Storage Keys
  // ---------------------------------------------------------------------------

  const sizeKey = useMemo(
    () => (storageKey ? buildStorageKey(storageKey) : null),
    [storageKey]
  );

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [paneSize, setPaneSize] = useState<number>(() => {
    if (size !== undefined) return size;
    return sizeKey ? readStoredSize(sizeKey, defaultSize) : defaultSize;
  });
  const [isDragging, setIsDragging] = useState(false);

  // ---------------------------------------------------------------------------
  // Refs
  // ---------------------------------------------------------------------------

  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const paneSizeRef = useRef(paneSize);

  // ---------------------------------------------------------------------------
  // Hooks
  // ---------------------------------------------------------------------------

  const isDesktop = useIsDesktop();

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const isHorizontal = orientation === 'horizontal';
  const isForcedMode = forcedSize !== undefined;
  const effectiveSize = isDesktop ? (isForcedMode ? forcedSize : paneSize) : defaultSize;

  // ---------------------------------------------------------------------------
  // Controlled size sync
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (size !== undefined && !isDragging) {
      paneSizeRef.current = size;
      setPaneSize(size);
    }
  }, [size, isDragging]);

  // ---------------------------------------------------------------------------
  // Container dimension helper
  // ---------------------------------------------------------------------------

  const getContainerDimension = useCallback((): number | null => {
    if (!containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    const dim = isHorizontal ? rect.width : rect.height;
    return dim > 0 ? dim : null;
  }, [isHorizontal]);

  // ---------------------------------------------------------------------------
  // Pointer Event Handlers
  // ---------------------------------------------------------------------------

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>): void => {
      // Only respond to primary pointer (left-click, primary touch)
      if (e.button !== 0 || !e.isPrimary) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      isDraggingRef.current = true;
      setIsDragging(true);
    },
    []
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>): void => {
      if (!isDraggingRef.current || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerDimension = isHorizontal
        ? containerRect.width
        : containerRect.height;
      const maxSizePx = parseMaxSize(maxSize, containerDimension);

      // Cap max against available space (container minus divider footprint),
      // reserving minSize for the second pane (symmetrical constraint).
      // Floored at zero to handle very small containers.
      const availableSize = Math.max(0, containerDimension - DIVIDER_FOOTPRINT);
      const cappedMax = Math.min(maxSizePx, Math.max(0, availableSize - minSize));

      // Calculate new size based on pointer position relative to container
      const newSize = isHorizontal
        ? e.clientX - containerRect.left
        : e.clientY - containerRect.top;

      // Clamp: if cappedMax < minSize (degenerate — container smaller than
      // minSize), clamp to cappedMax to avoid overflow beyond available space
      let clampedSize: number;
      if (cappedMax < minSize) {
        clampedSize = Math.max(0, Math.min(newSize, cappedMax));
      } else {
        clampedSize = Math.max(minSize, Math.min(newSize, cappedMax));
      }

      paneSizeRef.current = clampedSize;
      setPaneSize(clampedSize);
    },
    [isHorizontal, maxSize, minSize]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>): void => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      setIsDragging(false);
      e.currentTarget.releasePointerCapture(e.pointerId);
      if (sizeKey) writeStoredSize(sizeKey, paneSizeRef.current);
      const dim = getContainerDimension();
      if (dim) onResize?.(paneSizeRef.current, dim);
    },
    [sizeKey, getContainerDimension, onResize]
  );

  const handleLostPointerCapture = useCallback((): void => {
    if (!isDraggingRef.current) return;
    if (sizeKey) writeStoredSize(sizeKey, paneSizeRef.current);
    const dim = getContainerDimension();
    if (dim) onResize?.(paneSizeRef.current, dim);
    isDraggingRef.current = false;
    setIsDragging(false);
  }, [sizeKey, getContainerDimension, onResize]);

  // ---------------------------------------------------------------------------
  // Double-Click Reset
  // ---------------------------------------------------------------------------

  const handleDoubleClick = useCallback((): void => {
    const target = resetSize ?? defaultSize;
    paneSizeRef.current = target;
    setPaneSize(target);
    if (sizeKey) writeStoredSize(sizeKey, target);
    const dim = getContainerDimension();
    if (dim) onResize?.(target, dim);
  }, [resetSize, defaultSize, sizeKey, getContainerDimension, onResize]);

  // ---------------------------------------------------------------------------
  // Container Resize Normalization
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isDesktop || !containerRef.current || isForcedMode) return;

    const observer = new ResizeObserver((entries) => {
      // Skip normalization during active drag — drag handler clamps its own values
      if (isDraggingRef.current) return;

      const entry = entries[0];
      if (!entry) return;

      const containerDimension = isHorizontal
        ? entry.contentRect.width
        : entry.contentRect.height;

      // Guard zero-dimension containers (hidden/transitioning)
      if (containerDimension <= 0) return;

      const maxSizePx = parseMaxSize(maxSize, containerDimension);
      // Reserve minSize for second pane (symmetrical constraint)
      const availableSize = Math.max(0, containerDimension - DIVIDER_FOOTPRINT);
      const cappedMax = Math.min(maxSizePx, Math.max(0, availableSize - minSize));

      const prev = paneSizeRef.current;
      let clamped: number;
      if (cappedMax < minSize) {
        // Degenerate case: container smaller than minSize
        clamped = Math.max(0, Math.min(prev, cappedMax));
      } else {
        // Normal case: clamp to [minSize, cappedMax]
        clamped = Math.max(minSize, Math.min(prev, cappedMax));
      }
      if (clamped !== prev) {
        paneSizeRef.current = clamped;
        setPaneSize(clamped);
        onResize?.(clamped, containerDimension);
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isDesktop, isHorizontal, maxSize, minSize, onResize, isForcedMode]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div
      ref={containerRef}
      className={`relative flex h-full w-full min-h-0 ${
        isHorizontal ? 'flex-row' : 'flex-col'
      } ${isDragging ? 'select-none' : ''}`}
    >
      {/* First Pane */}
      <div
        className="shrink-0 min-h-0 min-w-0 overflow-hidden"
        style={
          isHorizontal
            ? { width: effectiveSize }
            : { height: effectiveSize }
        }
      >
        {firstContent}
      </div>

      {/* Divider — desktop only, hidden when forced */}
      {isDesktop && !isForcedMode && (
        <div
          className={`shrink-0 flex items-center justify-center group ${
            isHorizontal
              ? 'px-1 cursor-col-resize'
              : 'py-1 cursor-row-resize'
          }`}
          style={{ touchAction: 'none' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handleLostPointerCapture}
          onLostPointerCapture={handleLostPointerCapture}
          onDoubleClick={handleDoubleClick}
        >
          <div
            className={`rounded-full transition-colors ${
              isHorizontal ? 'w-1 h-full' : 'h-1 w-full'
            } ${
              isDragging
                ? 'bg-primary/50'
                : 'bg-default-200 group-hover:bg-primary/50'
            }`}
          />
        </div>
      )}

      {/* Second Pane */}
      <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
        {secondContent}
      </div>
    </div>
  );
};
